import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

export enum SequenceStatus {
  DRAFT = 'DRAFT',
  RUNNING = 'RUNNING',
  PAUSED = 'PAUSED',
  STOPPED = 'STOPPED',
  COMPLETED = 'COMPLETED',
}

export enum SequenceStepType {
  SEND_EMAIL = 'SEND_EMAIL',
  SEND_SMS = 'SEND_SMS',
  CALL = 'CALL',
  TEXT = 'TEXT',
  WAIT = 'WAIT',
}

export enum SequenceStepStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
}

export interface SequenceStepConfig {
  subject?: string
  body?: string
  htmlBody?: string
  days?: number
}

/** A step that isn't part of a sequence yet: a template's, or an automation's. */
export interface SequenceStepInput {
  stepType: SequenceStepType
  config: SequenceStepConfig
}

export interface SequenceStep {
  id: string
  order: number
  stepType: SequenceStepType
  config: SequenceStepConfig
  status: SequenceStepStatus
  scheduledFor: string | null
  completedAt: string | null
}

export enum SequenceStopReason {
  COMPLETED = 'COMPLETED',
  MANUAL = 'MANUAL',
  TERMINAL_STATUS = 'TERMINAL_STATUS',
  LEAD_REPLIED = 'LEAD_REPLIED',
}

export interface Sequence {
  id: string
  salesLead: string | null
  salesLeadName: string
  createdByName: string
  status: SequenceStatus
  dateExecuteStart: string
  startedAt: string | null
  stoppedAt: string | null
  stopReason: SequenceStopReason | null
  steps: SequenceStep[]
  createdAt: string
  updatedAt: string
}

/** Statuses where the sequence is still live (editable / runnable / paused). */
export const ACTIVE_SEQUENCE_STATUSES = [
  SequenceStatus.DRAFT,
  SequenceStatus.RUNNING,
  SequenceStatus.PAUSED,
] as const

const SEQ = '/api/v1/insure/internal/sequences'
const STEPS = '/api/v1/insure/internal/sequence-steps'

function listForLead(leadId: string): Promise<Sequence[]> {
  return axios.get(`${SEQ}/?sales_lead=${leadId}`).then((r) => toCamelCase(r.data) as Sequence[])
}

export function useLeadSequences(leadId: string) {
  return useQuery({
    queryKey: [CacheKeys.SEQUENCES, leadId],
    queryFn: () => listForLead(leadId),
    refetchInterval: 5000,
  })
}

/** The single active (DRAFT, RUNNING, or PAUSED) sequence for a lead, if any. */
export function activeSequence(sequences: Sequence[]): Sequence | undefined {
  return sequences.find((s) => (ACTIVE_SEQUENCE_STATUSES as readonly SequenceStatus[]).includes(s.status))
}

function listActive(): Promise<Sequence[]> {
  return axios.get(`${SEQ}/?active=true`).then((r) => toCamelCase(r.data) as Sequence[])
}

/** All active sequences across every lead — powers the sales-section Sequences page. */
export function useActiveSequences() {
  return useQuery({
    queryKey: [CacheKeys.SEQUENCES, 'active'],
    queryFn: listActive,
    refetchInterval: 5000,
  })
}

function getSequence(id: string): Promise<Sequence> {
  return axios.get(`${SEQ}/${id}/`).then((r) => toCamelCase(r.data) as Sequence)
}

/** One sequence with all its steps — powers the sequence detail page. */
export function useSequence(id: string) {
  return useQuery({
    queryKey: [CacheKeys.SEQUENCES, 'detail', id],
    queryFn: () => getSequence(id),
    refetchInterval: 5000,
  })
}

// Any sequence query (lead tab, Sequences page, detail page) may show the changed sequence.
function useSequenceMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.SEQUENCES] }),
  })
}

// The creating rep's IANA timezone, persisted in the sequence's metadata so the
// reminders/emails it spawns render times in their local zone (mirrors reminders).
const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/** Creates the lead's draft sequence, prefilled with a template's steps when one is given. */
export function useCreateSequence(leadId: string) {
  return useSequenceMutation((templateId?: string) =>
    axios.post(
      `${SEQ}/`,
      toSnakeCase({ salesLead: leadId, timezone: browserTimezone(), template: templateId ?? null })
    )
  )
}

export function useStartFromLast(leadId: string) {
  return useSequenceMutation(() =>
    axios.post(`${SEQ}/start-from-last/`, toSnakeCase({ salesLead: leadId, timezone: browserTimezone() }))
  )
}

/** Replaces a draft's steps with copies of a template's. */
export function useApplySequenceTemplate() {
  return useSequenceMutation(({ id, templateId }: { id: string; templateId: string }) =>
    axios.post(`${SEQ}/${id}/apply-template/`, { template: templateId })
  )
}

export function useStartSequence() {
  return useSequenceMutation(({ id, dateExecuteStart }: { id: string; dateExecuteStart: string }) =>
    axios.post(`${SEQ}/${id}/start/`, toSnakeCase({ dateExecuteStart }))
  )
}

export function useStopSequence() {
  return useSequenceMutation((id: string) => axios.post(`${SEQ}/${id}/stop/`))
}

export function usePauseSequence() {
  return useSequenceMutation((id: string) => axios.post(`${SEQ}/${id}/pause/`))
}

export function useResumeSequence() {
  return useSequenceMutation((id: string) => axios.post(`${SEQ}/${id}/resume/`))
}

export function useReorderSteps() {
  return useSequenceMutation(({ id, orderedIds }: { id: string; orderedIds: string[] }) =>
    axios.post(`${SEQ}/${id}/reorder/`, toSnakeCase({ orderedIds }))
  )
}

export function useAddStep() {
  return useSequenceMutation(
    (data: { sequence: string; stepType: SequenceStepType; config: SequenceStepConfig }) =>
      axios.post(`${STEPS}/`, toSnakeCase(data))
  )
}

export function useUpdateStep() {
  return useSequenceMutation(({ id, config }: { id: string; config: SequenceStepConfig }) =>
    axios.patch(`${STEPS}/${id}/`, toSnakeCase({ config }))
  )
}

export function useDeleteStep() {
  return useSequenceMutation((id: string) => axios.delete(`${STEPS}/${id}/`))
}
